import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import ContentProtection from "@/components/ContentProtection";
import remarkGfm from "remark-gfm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, ChevronLeft, ChevronDown, ArrowDown, BookOpen, Sparkles, Newspaper, AlertTriangle, Brain, Target, ListChecks, Zap, GraduationCap, Trophy, Clock, Lock, Crown, ArrowRight, Network } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { API_BASE } from "@/services/api";
import useHeartbeat from "@/hooks/useHeartbeat";
import { useAuth } from "@/auth/AuthContext";
import { useExam } from "@/context/ExamContext";

// Lazy-load 3D India map (heavier chunk — only loads for Geography subject)
const IndiaMap3D = lazy(() => import("@/components/three/IndiaMap3D"));
// Lazy-load 2D Mind Map (NotebookLM-style hierarchical view)
const MindMap2D = lazy(() => import("@/components/three/MindMap2D"));
// One-page Brain Map + 2-page Rapid Revision Sheet (chapter-end revision)
const BrainMap = lazy(() => import("@/components/prelims/BrainMap"));
const RapidRevisionSheet = lazy(() => import("@/components/prelims/RapidRevisionSheet"));

// API_BASE = '/api' in production (same-origin → no CORS) and the preview
// URL on localhost. We never hardcode REACT_APP_BACKEND_URL here — that
// causes cross-origin calls and CORS failures on production (clarias.in).
function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return axios({
    url: `${API_BASE}${path}`,
    headers: { Authorization: token ? `Bearer ${token}` : undefined, ...(opts.headers || {}) },
    ...opts,
  }).then((r) => r.data);
}

// =================== Subjects List Page ===================
export const PrelimsMasterSubjectsPage = () => {
  const navigate = useNavigate();
  const { selectedExam } = useExam();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api(`/prelims/master/subjects?exam=${encodeURIComponent(selectedExam || "")}`)
      .then((d) => setSubjects(d.subjects || []))
      .finally(() => setLoading(false));
  }, [selectedExam]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/prelims")}
          data-testid="prelims-master-back-btn"
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Prelims Hub
        </Button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Master Material
            </h1>
          </div>
          <p className="text-slate-600 max-w-2xl text-sm sm:text-base">
            Premium chapter-wise study material for UPSC Prelims — built by subject experts, refreshed daily with current affairs.
          </p>
          <button
            onClick={() => navigate("/mindmap")}
            data-testid="prelims-master-mindmap-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 shadow-sm"
          >
            <Network className="w-4 h-4" /> Explore as Mind Maps
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-48 rounded-2xl" />
            ))}
          </div>
        ) : (() => {
          const isTG = (s) => s.id.startsWith("telangana");
          const general = subjects.filter((s) => !isTG(s));
          const telangana = subjects.filter(isTG);
          const renderTile = (s) => {
            const comingSoon = !(s.chapters_cached > 0);
            return (
              <button
                key={s.id}
                data-testid={`subject-tile-${s.id}`}
                onClick={() => { if (!comingSoon) navigate(`/prelims/master/${s.id}`); }}
                disabled={comingSoon}
                className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.color} p-6 sm:p-8 text-left text-white shadow-lg transition-all ${
                  comingSoon ? "opacity-60 cursor-not-allowed grayscale-[35%]" : "hover:shadow-2xl hover:-translate-y-1"
                }`}
              >
                {comingSoon && (
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
                    <span className="px-3 py-1.5 rounded-full bg-white/90 text-slate-800 text-xs font-bold flex items-center gap-1.5 shadow">
                      <Lock className="w-3.5 h-3.5" /> Coming Soon
                    </span>
                  </div>
                )}
                <div className="text-5xl mb-3">{s.icon}</div>
                <h2 className="text-2xl font-bold mb-2">{s.title}</h2>
                <div className="flex items-center gap-3 text-sm opacity-90 mb-4">
                  <span>{s.topic_count} topics</span>
                  <span>•</span>
                  <span>{s.chapter_count} chapters</span>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <Badge variant="secondary" className="bg-white/20 text-white border-0">
                    {comingSoon ? "Coming Soon" : `${s.chapters_cached}/${s.chapter_count} ready`}
                  </Badge>
                  {!comingSoon && <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                </div>
              </button>
            );
          };
          return (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {general.map(renderTile)}
              </div>
              {telangana.length > 0 && (
                <div className="mt-14" data-testid="telangana-section">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-slate-200" />
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 whitespace-nowrap">
                      <span className="text-xl">📍</span> Telangana
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">SI Special</span>
                    </h2>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-200" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {telangana.map(renderTile)}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

// =================== Subject (Topics & Chapters) Page ===================
export const PrelimsMasterSubjectPage = () => {
  const navigate = useNavigate();
  const { subjectId } = useParams();
  const { user } = useAuth();
  const { selectedExam, isPro } = useExam();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api(`/prelims/master/subject/${subjectId}?exam=${encodeURIComponent(selectedExam || "")}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [subjectId, selectedExam]);

  if (loading) {
    return (
      <div className="min-h-screen p-8 max-w-5xl mx-auto">
        <Skeleton className="h-8 w-1/3 mb-4" />
        <Skeleton className="h-32 mb-2" />
      </div>
    );
  }
  if (!data) return <div className="p-8">Subject not found.</div>;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/prelims/master")}
          data-testid="subject-back-btn"
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> All Subjects
        </Button>

        <div className={`rounded-2xl p-6 sm:p-8 mb-8 bg-gradient-to-br ${data.color} text-white shadow-lg`}>
          <div className="text-5xl mb-2">{data.icon}</div>
          <h1 className="text-3xl sm:text-4xl font-bold">{data.title}</h1>
          <p className="opacity-90 mt-2 text-sm">{data.topics.length} topics</p>
        </div>

        {/* Polity Radial Mind Maps — featured entry at the top of the subject */}
        {subjectId === "polity" && (
          <button
            type="button"
            onClick={() => navigate("/prelims/polity-radial-mindmaps")}
            data-testid="polity-radial-mindmaps-entry"
            className="group w-full text-left mb-8 flex items-center gap-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-5 sm:p-6 shadow-sm transition-all hover:shadow-md hover:border-amber-300"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-200 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-600">Interactive</span>
                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-100/60">New</Badge>
              </div>
              <div className="text-base sm:text-lg font-bold text-slate-900">Polity Radial Mind Maps</div>
              <p className="text-sm text-slate-600 mt-0.5">
                Revise the whole syllabus visually — chapter-wise radial mind maps in a distraction-free viewer.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-600 flex-shrink-0 transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {/* 3D India Map — only rendered for the Geography subject */}
        {subjectId === "geography" && (
          <Card className="mb-8 overflow-hidden border-slate-200 shadow-md">
            <CardHeader className="bg-slate-900/95 border-b border-slate-800 py-3 px-5">
              <CardTitle className="text-sm sm:text-base flex items-center gap-2 text-orange-300">
                <Sparkles className="w-4 h-4" /> Interactive India · 3D
                <Badge variant="outline" className="ml-auto text-[10px] border-orange-300/40 text-orange-200 bg-orange-500/10">Beta</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Suspense fallback={
                <div className="h-[460px] flex items-center justify-center bg-slate-950 text-slate-500 text-xs">
                  Loading 3D map…
                </div>
              }>
                <IndiaMap3D height={460} />
              </Suspense>
            </CardContent>
          </Card>
        )}

        <div className="space-y-6">
          {data.topics.map((t) => (
            <Card key={t.id} className="overflow-hidden">
              <CardHeader className="bg-slate-100/60 border-b">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-slate-800">
                  <ListChecks className="w-4 h-4 text-indigo-500" />
                  {t.title}
                  <Badge variant="outline" className="ml-2 text-[10px]">{t.chapters.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {t.chapters.map((ch) => {
                    const isLockedForFree = !isPro && !ch.is_free_preview;
                    return (
                      <button
                        key={ch.id}
                        data-testid={`chapter-link-${ch.id}`}
                        onClick={() => navigate(`/prelims/master/${subjectId}/${ch.id}`)}
                        disabled={!ch.is_cached}
                        className={`w-full text-left flex items-center justify-between px-5 py-3.5 hover:bg-indigo-50 transition-colors ${
                          ch.is_cached ? "" : "opacity-50 cursor-not-allowed"
                        }`}
                      >
                        <span className="text-sm sm:text-base text-slate-800 font-medium flex items-center gap-2">
                          {ch.title}
                          {ch.is_free_preview && (
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"
                              data-testid={`chapter-free-badge-${ch.id}`}
                            >
                              Free
                            </Badge>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          {!ch.is_cached ? (
                            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                              coming soon
                            </Badge>
                          ) : isLockedForFree ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-amber-50 text-amber-800 border-amber-300 inline-flex items-center gap-1"
                              data-testid={`chapter-pro-badge-${ch.id}`}
                            >
                              <Lock className="w-3 h-3" /> PRO
                            </Badge>
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// =================== Chapter Reader Page ===================
const SectionCard = ({ title, icon: Icon, color = "indigo", children }) => (
  <Card className={`border-${color}-100`}>
    <CardHeader className="pb-3">
      <CardTitle className={`text-base sm:text-lg flex items-center gap-2 text-${color}-800`}>
        {Icon && <Icon className={`w-5 h-5 text-${color}-600`} />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">
      {children}
    </CardContent>
  </Card>
);

// ── Auto-bulletize (display-time) ──────────────────────────────────────────
// Converts prose paragraphs in deep-study markdown into bullet points so every
// chapter reads like clean revision notes. Preserves tables, headings, existing
// bullets/numbered lists, blockquotes and standalone bold sub-headings.
// Enabled per-subject (History) via the `bulletize` prop — no data mutation.
const _ABBREV_RE = /\b(Dr|Mr|Mrs|Ms|Prof|Sr|Jr|vs|etc|Inc|Ltd|Co|St|Fig|No|Vol|Ch|Rev|approx|Govt|Dept|Est|e\.g|i\.e|c\.f|A\.D|B\.C|C\.E|B\.C\.E|A\.D\.E|U\.S|U\.K|Mt)\./gi;

const _splitSentences = (text) => {
  let t = text.trim();
  if (!t) return [];
  const DOT = "\u0001";
  const MARK = "\u0002";
  // Protect single-letter initials ("V." in "V. S. Wakankar") and circa ("c. 2600")
  t = t.replace(/\b([A-Z])\./g, "$1" + DOT);
  t = t.replace(/\b(c|ca)\.(\s*\d)/gi, "$1" + DOT + "$2");
  t = t.replace(_ABBREV_RE, (m) => m.replace(/\./g, DOT));
  // Mark sentence boundaries: . ! ? + space(s) + a capital / quote / paren / digit
  t = t.replace(/([.!?])\s+(["“'(A-Z0-9])/g, "$1" + MARK + "$2");
  const parts = t.split(MARK);
  return parts
    .map((s) => s.split(DOT).join(".").trim())
    .filter(Boolean);
};

// --- Visual Learning flowchart -------------------------------------------
// "Visual Learning" sections store arrow-chains (e.g. "Early Harappan → surplus
// → Mature Harappan urbanisation → ...") using → or ->. We render these as a
// clean vertical flowchart instead of a flat run-on line.
const _ARROW_SPLIT_RE = /\s*(?:→|⇒|-->|=>|->)\s*/;
const isArrowChain = (text) => {
  if (!text || typeof text !== "string") return false;
  const hits = (text.match(/→|⇒|-->|=>|->/g) || []).length;
  return hits >= 2;
};
// Flatten ReactMarkdown children into a plain string.
const nodeToText = (children) => {
  if (children == null) return "";
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(nodeToText).join("");
  if (children.props && children.props.children != null) return nodeToText(children.props.children);
  return "";
};
const _YEAR_RE = /^\s*(c\.?\s*)?(\d{3,4}(?:\s*[–-]\s*\d{3,4})?\s*(?:BCE|BC|CE|AD)?)\b\s*(.*)$/i;
const FlowChain = ({ text }) => {
  const steps = text.split(_ARROW_SPLIT_RE).map((s) => s.trim()).filter(Boolean);
  if (steps.length < 2) return <p className="my-3 leading-7">{text}</p>;
  return (
    <div className="my-5 flex flex-col items-stretch gap-0" data-testid="visual-flowchart">
      {steps.map((step, i) => {
        const m = step.match(_YEAR_RE);
        const year = m && m[2] ? `${m[1] ? "c. " : ""}${m[2]}`.trim() : null;
        const label = year ? (m[3] || "").trim() : step;
        return (
          <div key={i} className="flex flex-col items-center">
            <div
              className="w-full max-w-2xl rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white px-4 py-3 shadow-sm"
              data-testid={`flowchart-step-${i}`}
            >
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-bold text-white">
                  {i + 1}
                </span>
                <div className="min-w-0 text-sm sm:text-[15px] leading-6 text-slate-800">
                  {year && (
                    <span className="mr-2 inline-block rounded-md bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">
                      {year}
                    </span>
                  )}
                  {label || step}
                </div>
              </div>
            </div>
            {i < steps.length - 1 && (
              <ArrowDown className="my-1 h-4 w-4 text-indigo-400" aria-hidden="true" />
            )}
          </div>
        );
      })}
    </div>
  );
};

const bulletizeMarkdown = (md) => {
  if (!md || typeof md !== "string") return md;
  // If the section is already well-structured (has several bullets, like the
  // AI-reformatted Executive Summary or hand-authored chapters), leave it as-is
  // so we don't flatten its intro paragraph / crisp bold-lead bullets.
  const existingBullets = (md.match(/(^|\n)\s*[-*+]\s+/g) || []).length;
  if (existingBullets >= 4) return md;
  const lines = md.split("\n");
  const out = [];
  let para = [];
  const flush = () => {
    if (!para.length) return;
    const paragraph = para.join(" ").trim();
    para = [];
    if (!paragraph) return;
    const sents = _splitSentences(paragraph);
    (sents.length ? sents : [paragraph]).forEach((s) => out.push(`- ${s}`));
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    const t = line.trim();
    if (t === "") { flush(); out.push(""); continue; }
    const special =
      t.startsWith("|") ||                  // table
      t.startsWith("#") ||                  // heading
      t.startsWith(">") ||                  // blockquote
      t.startsWith("```") ||                // code fence
      /^([-*+])\s+/.test(t) ||              // existing bullet
      /^\d+[.)]\s+/.test(t) ||              // numbered list
      isArrowChain(t) ||                     // flowchart arrow-chain → keep as own paragraph
      /^\*\*[^*]+\*\*:?$/.test(t);          // standalone bold sub-heading
    if (special) { flush(); out.push(line); continue; }
    para.push(t);                           // prose → accumulate
  }
  flush();
  return out.join("\n");
};

// Explicit table renderers — a real bordered, full-width grid wrapped for
// horizontal scroll on narrow screens (prose-* table modifiers were producing
// borderless, content-width tables that floated and wrapped awkwardly).
const mdComponents = {
  table: ({ node, ...props }) => (
    <div className="my-4 w-full overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-indigo-50" {...props} />,
  th: ({ node, ...props }) => (
    <th className="border border-slate-200 px-3 py-2 text-left align-top font-semibold text-slate-900" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-slate-200 px-3 py-2 align-top text-slate-700" {...props} />
  ),
  tr: ({ node, ...props }) => <tr className="even:bg-slate-50/60" {...props} />,
  p: ({ node, children, ...props }) => {
    const text = nodeToText(children);
    if (isArrowChain(text)) return <FlowChain text={text} />;
    return <p {...props}>{children}</p>;
  },
  li: ({ node, children, ...props }) => {
    const text = nodeToText(children);
    if (isArrowChain(text)) return <li className="list-none -ml-5" {...props}><FlowChain text={text} /></li>;
    return <li {...props}>{children}</li>;
  },
};

// Markdown rendering wrapper — Tailwind-styled prose
const Md = ({ children, bulletize = false }) => (
  <div className="prose prose-slate prose-sm sm:prose-base max-w-none
                  prose-headings:font-semibold prose-headings:text-slate-900
                  prose-h3:text-base prose-h3:sm:text-lg prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-indigo-900
                  prose-h4:text-sm prose-h4:sm:text-base prose-h4:mt-4 prose-h4:mb-1.5
                  prose-p:my-3 prose-p:leading-7
                  prose-strong:text-slate-900 prose-strong:font-semibold
                  prose-ul:my-3 prose-li:my-1 prose-li:leading-7">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
      {bulletize ? bulletizeMarkdown(children || "") : (children || "")}
    </ReactMarkdown>
  </div>
);

// One collapsible deep-study section
const DeepStudySection = ({ section, defaultOpen = false, index, bulletize = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="border border-indigo-100 rounded-xl bg-white overflow-hidden transition-shadow hover:shadow-sm"
      data-testid={`deep-study-section-${section.section_id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-indigo-50/40 transition-colors"
        aria-expanded={open}
        data-testid={`deep-study-toggle-${section.section_id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex-shrink-0 w-6 h-6 rounded-md bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">
            {index + 1}
          </span>
          <h3 className="text-sm sm:text-[15px] font-semibold text-slate-900 truncate">
            {section.title}
          </h3>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 pt-1 border-t border-indigo-50">
          <Md bulletize={bulletize}>{section.content_markdown}</Md>
        </div>
      )}
    </div>
  );
};

// Full deep-study block — At a Glance + accordioned sections
const DeepStudyBlock = ({ deepStudy, readMinutes, bulletize = false }) => {
  const sections = Array.isArray(deepStudy) ? deepStudy : [];
  return (
    <Card className="border-indigo-100">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-indigo-800">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Concept Overview
          </CardTitle>
          {readMinutes ? (
            <Badge variant="outline" className="text-[11px] text-slate-600 border-slate-300 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {readMinutes} min read
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="text-sm sm:text-[15px] text-slate-700 leading-relaxed space-y-5">
        {sections.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600 mb-2">
              Deep Study · {sections.length} sections
            </div>
            <div className="space-y-2">
              {sections.map((s, i) => (
                <DeepStudySection
                  key={s.section_id || i}
                  section={s}
                  index={i}
                  defaultOpen={i < 2}
                  bulletize={bulletize}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const PrelimsMasterChapterPage = () => {
  const navigate = useNavigate();
  const { subjectId, chapterId } = useParams();
  const [data, setData] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mcqAnswers, setMcqAnswers] = useState({});

  // Log a chapter_read activity so the user gets streak credit just for reading.
  useHeartbeat({ activity: "chapter_read", module: "prelims", contentId: chapterId });

  useEffect(() => {
    setLoading(true);
    setPaywall(null);
    setData(null);
    api(`/prelims/master/chapter/${chapterId}`)
      .then(setData)
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 403 && detail?.locked) {
          setPaywall(detail);
        }
      })
      .finally(() => setLoading(false));
  }, [chapterId]);

  if (loading) {
    return <div className="min-h-screen p-8 max-w-4xl mx-auto"><Skeleton className="h-12 w-2/3 mb-6" /><Skeleton className="h-40" /></div>;
  }

  if (paywall) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-purple-50/60 p-4 sm:p-8"
        data-testid="prelims-chapter-paywall-page"
      >
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(`/prelims/master/${subjectId}`)}
            data-testid="chapter-paywall-back-btn"
            className="mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Subject
          </Button>

          <Card className="border-2 border-purple-200 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center mb-3">
                <Lock className="w-7 h-7 text-white" />
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold mx-auto mb-2 w-fit">
                <Crown className="w-3.5 h-3.5" />
                CLARIAS PRO ONLY
              </div>
              <CardTitle
                className="text-2xl font-bold text-slate-900"
                data-testid="prelims-chapter-paywall-title"
              >
                {paywall.chapter_title || "This chapter"} is locked
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 text-center">
              <p
                className="text-slate-600 text-sm leading-relaxed"
                data-testid="prelims-chapter-paywall-description"
              >
                {paywall.description}
              </p>

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-xl p-4 text-left">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-semibold text-purple-900 uppercase tracking-wide">
                    What you unlock with PRO
                  </span>
                </div>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  <li>• All 154 chapters of rubric-graded Prelims study material</li>
                  <li>• 8-section deep-study per chapter (~1,700 words each)</li>
                  <li>• Live current-affairs strip auto-tagged to every chapter</li>
                  <li>• Practice MCQs, common traps, mind-map summaries</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold"
                  onClick={() => navigate(paywall.cta_route || "/upgrade")}
                  data-testid="prelims-chapter-paywall-upgrade-button"
                >
                  {paywall.cta_text || "Upgrade to PRO"}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate(`/prelims/master/${subjectId}`)}
                  data-testid="prelims-chapter-paywall-back-button"
                >
                  Try a free chapter
                </Button>
              </div>
              <p className="text-xs text-slate-400 pt-1">
                Plans start at ₹699 / month · cancel anytime
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data || !data.content) return <div className="p-8">Chapter content not yet available.</div>;

  const c = data.content;

  return (
    <ContentProtection>
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(`/prelims/master/${subjectId}`)}
          data-testid="chapter-back-btn"
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Subject
        </Button>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 mb-6">
          <Badge variant="outline" className="mb-3">Chapter</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">{c.title}</h1>
        </div>

        {/* Radial Mind Maps CTA — only on the "Mind Maps & Visual Revision" chapter */}
        {chapterId === "mind-maps-visual-revision" && (
          <button
            type="button"
            onClick={() => navigate("/prelims/polity-radial-mindmaps")}
            data-testid="radial-mindmaps-cta"
            className="group w-full text-left mb-6 flex items-center gap-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-5 sm:p-6 shadow-sm transition-all hover:shadow-md hover:border-amber-300"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-200 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-600 mb-0.5">
                Interactive
              </div>
              <div className="text-base sm:text-lg font-bold text-slate-900">Open Radial Mind Maps</div>
              <p className="text-sm text-slate-600 mt-0.5">
                Explore chapter-wise radial mind maps in a distraction-free visual revision viewer.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-600 flex-shrink-0 transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {/* Live CA strip */}
        {data.live_ca && data.live_ca.length > 0 && (
          <Card className="mb-6 border-rose-200 bg-gradient-to-r from-rose-50 to-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-rose-700">
                <Newspaper className="w-5 h-5" />
                Recent News on This Topic
                <Badge variant="secondary" className="ml-2 bg-rose-100 text-rose-700 border-0">
                  {data.live_ca.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {data.live_ca.map((item, i) => (
                <div key={i} className="text-sm text-slate-700 flex items-start gap-2" data-testid={`live-ca-${i}`}>
                  <span className="text-rose-500 font-mono text-xs flex-shrink-0 mt-0.5">{item.date}</span>
                  <span className="line-clamp-2">{item.headline}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Concept Overview — At a Glance + Deep Study (8-section accordion) */}
        <div className="space-y-6">
          <DeepStudyBlock
            deepStudy={c.deep_study_lean || c.deep_study}
            readMinutes={c.estimated_read_minutes}
            bulletize={subjectId === "history"}
          />

          {/* ── Chapter-end revision: One-Page Brain Map + Rapid Revision Sheet ──
              Key Facts, PYQ Coverage, Conceptual Linkages, Common Traps and
              Quick Revision are consolidated into the single Rapid Revision
              Sheet below (no duplication). */}
          {(c.mind_map_tree || c.mind_map_summary) && (
            <Suspense fallback={null}>
              <BrainMap
                title={c.title || data?.title || "Chapter"}
                tree={c.mind_map_tree}
                fallbackBullets={c.mind_map_summary}
              />
            </Suspense>
          )}

          <Suspense fallback={null}>
            <RapidRevisionSheet
              title={c.title || data?.title || "Chapter"}
              keyFacts={c.key_facts}
              commonTraps={c.common_traps}
              pyq={c.pyq_coverage_map}
              quickNotes={c.quick_revision_notes}
              linkages={c.conceptual_linkages}
            />
          </Suspense>

          {/* Practice MCQs */}
          {Array.isArray(c.practice_mcqs) && c.practice_mcqs.length > 0 && (
            <Card className="border-blue-100">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-blue-800">
                  <Target className="w-5 h-5 text-blue-600" />
                  Practice MCQs (5)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {c.practice_mcqs.map((q, i) => {
                  const userAnswer = mcqAnswers[i];
                  const isCorrect = userAnswer === q.correct_answer;
                  return (
                    <div key={i} className="border-b last:border-0 pb-5 last:pb-0">
                      <div className="text-sm font-medium text-slate-800 mb-3">
                        <span className="text-blue-500 mr-1">Q{i + 1}.</span> {q.question}
                      </div>
                      <div className="space-y-1.5">
                        {Object.entries(q.options || {}).map(([k, v]) => {
                          const picked = userAnswer === k;
                          const reveal = userAnswer !== undefined;
                          const isThisCorrect = k === q.correct_answer;
                          let cls = "border-slate-200 hover:border-blue-300 hover:bg-blue-50";
                          if (reveal && picked && !isCorrect) cls = "border-rose-300 bg-rose-50 text-rose-800";
                          if (reveal && isThisCorrect) cls = "border-emerald-300 bg-emerald-50 text-emerald-800";
                          return (
                            <button
                              key={k}
                              data-testid={`mcq-${i}-option-${k}`}
                              onClick={() => setMcqAnswers((m) => ({ ...m, [i]: k }))}
                              disabled={reveal}
                              className={`w-full text-left text-sm border rounded-lg px-3 py-2 transition-colors ${cls}`}
                            >
                              <strong className="mr-2">{k}.</strong>{v}
                            </button>
                          );
                        })}
                      </div>
                      {userAnswer !== undefined && (
                        <div className={`mt-3 text-xs sm:text-sm rounded-lg p-3 ${isCorrect ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
                          <strong>{isCorrect ? "✅ Correct." : "❌ Incorrect."}</strong> {q.explanation}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="text-xs text-slate-400 mt-8 text-center">
          Powered by CLARIAS · Master Material refreshed via Daily CA layer
        </div>
      </div>
    </div>
    </ContentProtection>
  );
};
